// src/hooks/useAuth.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await authService.getProfile();
        setUser(response.user);
        // Store tenant details separately for easy access
        if (response.tenant) {
          setTenant(response.tenant);
          // Also store in localStorage for receipt access
          localStorage.setItem('businessName', response.tenant.business_name);
          localStorage.setItem('businessPhone', response.user.phone || '');
          localStorage.setItem('businessEmail', response.user.email || '');
          localStorage.setItem('businessAddress', response.tenant.address || '');
          localStorage.setItem('businessPin', response.tenant.tin_number || '');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
        localStorage.removeItem('businessName');
        localStorage.removeItem('businessPhone');
        localStorage.removeItem('businessEmail');
        localStorage.removeItem('businessAddress');
        localStorage.removeItem('businessPin');
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    const response = await authService.login(credentials);
    const { accessToken, refreshToken, user, tenant: tenantData } = response;
    
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('tenantId', user.tenant_id);
    
    // Store business details for receipt printing
    if (tenantData) {
      localStorage.setItem('businessName', tenantData.business_name);
      localStorage.setItem('businessPhone', user.phone || '');
      localStorage.setItem('businessEmail', user.email || '');
      localStorage.setItem('businessAddress', tenantData.address || '');
      localStorage.setItem('businessPin', tenantData.tin_number || '');
      setTenant(tenantData);
    } else if (user.business_name) {
      localStorage.setItem('businessName', user.business_name);
      localStorage.setItem('businessPhone', user.phone || '');
      localStorage.setItem('businessEmail', user.email || '');
      localStorage.setItem('businessAddress', user.address || '');
      localStorage.setItem('businessPin', user.tin_number || '');
    }
    
    setUser(user);
    
    return response;
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await authService.logout({ refreshToken });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    
    // Clear all storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('businessName');
    localStorage.removeItem('businessPhone');
    localStorage.removeItem('businessEmail');
    localStorage.removeItem('businessAddress');
    localStorage.removeItem('businessPin');
    
    setUser(null);
    setTenant(null);
    navigate('/login');
  };

  const updateProfile = async (data) => {
    const response = await authService.updateProfile(data);
    setUser(prev => ({ ...prev, ...response.user }));
    
    // Update stored business details
    if (response.user) {
      if (response.user.business_name) {
        localStorage.setItem('businessName', response.user.business_name);
      }
      if (response.user.phone) {
        localStorage.setItem('businessPhone', response.user.phone);
      }
      if (response.user.email) {
        localStorage.setItem('businessEmail', response.user.email);
      }
      if (response.user.address) {
        localStorage.setItem('businessAddress', response.user.address);
      }
      if (response.user.tin_number) {
        localStorage.setItem('businessPin', response.user.tin_number);
      }
    }
    
    return response;
  };

  const changePassword = async (data) => {
    return await authService.changePassword(data);
  };

  // Helper to get business details for receipts
  const getBusinessDetails = () => {
    return {
      name: localStorage.getItem('businessName') || user?.business_name || 'Brominon',
      phone: localStorage.getItem('businessPhone') || user?.phone || '+254 727 332 761',
      email: localStorage.getItem('businessEmail') || user?.email || 'info@brominon.com',
      address: localStorage.getItem('businessAddress') || user?.address || 'Nairobi, Kenya',
      pin: localStorage.getItem('businessPin') || user?.tin_number || 'N/A'
    };
  };

  const value = {
    user,
    tenant,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    getBusinessDetails,
    isAuthenticated: !!user,
    hasRole: (role) => user?.role === role || user?.role === 'superadmin',
    hasPermission: (permission) => {
      const permissions = {
        superadmin: ['*'],
        admin: ['manage_users', 'manage_settings', 'view_reports', 'create_sale', 'manage_inventory', 'manage_payments'],
        accountant: ['view_reports', 'manage_expenses', 'manage_payments'],
        cashier: ['create_sale'],
        shop_operator: ['manage_inventory']
      };
      const userPermissions = permissions[user?.role] || [];
      return userPermissions.includes('*') || userPermissions.includes(permission);
    }
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}